import { useState, useEffect, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  Smartphone,
  Sparkles,
  LogOut,
  ArrowLeft,
  RefreshCw,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Brand({ sub }) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={`${process.env.PUBLIC_URL || ""}/assets/mask-logo.png`}
        alt="Toji Peptides"
        className="h-9 w-9 object-contain brightness-0 invert"
      />
      <span className="font-display text-xl font-black uppercase tracking-[0.16em] text-white">
        TOJI<span className="text-[#d91616]">PEP</span>
      </span>
      {sub && (
        <span className="font-mono2 ml-1 hidden text-[10px] uppercase tracking-[0.3em] text-white/40 sm:inline">
          {sub}
        </span>
      )}
    </div>
  );
}

function MemberLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotice("");
    try {
      const res = await fetch(`${API}/members/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNotice(typeof data.detail === "string" ? data.detail : "Unable to sign in.");
        return;
      }
      onLogin();
    } catch {
      setNotice("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <form
        onSubmit={submit}
        data-testid="member-login-form"
        className="w-full max-w-sm rounded-2xl border border-[#1c1c1c] bg-[#0c0c0c] p-8"
      >
        <div className="mb-8">
          <Brand />
          <p className="font-mono2 mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40">
            <Lock size={12} className="text-[#d91616]" /> Members Only
          </p>
        </div>

        <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
          Email
        </label>
        <input
          data-testid="member-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-5 w-full border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none focus:border-[#d91616]"
          placeholder="you@domain.com"
        />
        <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
          Password
        </label>
        <input
          data-testid="member-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-6 w-full border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none focus:border-[#d91616]"
          placeholder="••••••••"
        />

        {notice && (
          <p
            data-testid="member-login-notice"
            className="mb-5 rounded-lg border border-[#d91616]/30 bg-[#d91616]/10 px-3 py-2.5 font-body text-xs leading-relaxed text-[#ff8a8a]"
          >
            {notice}
          </p>
        )}

        <button
          data-testid="member-login-btn"
          type="submit"
          disabled={loading}
          className="w-full bg-[#d91616] py-3 font-mono2 text-xs uppercase tracking-[0.3em] text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Enter the Dojo"}
        </button>

        <div className="mt-6 flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/40">
          <Link to="/" data-testid="member-back-home" className="hover:text-white">
            ← Back to site
          </Link>
          <Link to="/#contact" data-testid="member-request-link" className="text-[#d91616] hover:text-white">
            Request access
          </Link>
        </div>
      </form>
    </div>
  );
}

const services = [
  {
    key: "shop",
    icon: ShoppingBag,
    title: "The Shop",
    desc: "Members-only catalogue and ordering. Opening soon.",
  },
  {
    key: "app",
    icon: Smartphone,
    title: "App Portal",
    desc: "Your gateway to the Toji companion app. Opening soon.",
  },
  {
    key: "services",
    icon: Sparkles,
    title: "Member Services",
    desc: "Protocols, concierge support and more. Opening soon.",
  },
];

function Lounge({ member, onLogout }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="sticky top-0 z-10 border-b border-[#141414] bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4">
          <Brand sub="Members" />
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="lounge-home"
              className="flex items-center gap-1.5 border border-[#2a2a2a] px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/70 hover:border-white"
            >
              <ArrowLeft size={13} /> Site
            </Link>
            <button
              onClick={onLogout}
              data-testid="lounge-logout"
              className="flex items-center gap-1.5 border border-[#2a2a2a] px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/70 hover:border-[#d91616] hover:text-[#d91616]"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-16 md:py-24">
        <p className="font-mono2 mb-3 text-xs uppercase tracking-[0.4em] text-[#d91616]">
          Welcome back
        </p>
        <h1 className="font-display text-4xl font-black leading-[0.95] tracking-tighter md:text-6xl">
          {member?.name || "Ronin"}.
        </h1>
        <p className="mt-4 max-w-lg font-body text-sm leading-relaxed text-white/50">
          You're inside the Toji dojo. Your member services are being forged —
          each will open here as it goes live.
        </p>

        <div data-testid="lounge-services" className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                data-testid={`service-${s.key}`}
                className="group relative overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#0c0c0c] p-6 transition-colors hover:border-[#d91616]/40"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl border border-[#d91616]/40 text-[#d91616]">
                  <Icon size={22} />
                </span>
                <h3 className="font-display mt-6 text-2xl font-black tracking-tight text-white">
                  {s.title}
                </h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{s.desc}</p>
                <span className="font-mono2 mt-6 inline-block rounded-full border border-[#2a2a2a] px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-white/40">
                  Coming soon
                </span>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default function MembersLounge() {
  const [authed, setAuthed] = useState(null);
  const [member, setMember] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch(`${API}/members/me`, { credentials: "include" });
      if (res.ok) {
        setMember(await res.json());
        setAuthed(true);
      } else {
        setAuthed(false);
      }
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const logout = async () => {
    try {
      await fetch(`${API}/members/logout`, { method: "POST", credentials: "include" });
    } catch {
      /* ignore */
    }
    setAuthed(false);
    setMember(null);
  };

  if (authed === null) {
    return (
      <div
        data-testid="member-checking"
        className="flex min-h-screen items-center justify-center bg-[#050505]"
      >
        <RefreshCw size={22} className="animate-spin text-[#d91616]" />
      </div>
    );
  }

  if (!authed) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <MemberLogin onLogin={checkSession} />
      </>
    );
  }

  return <Lounge member={member} onLogout={logout} />;
}
